'use client';

import { Typography, IcStarFill, IcChevronRight, theme, Flex, SicInfoCircle } from '@bgzt/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { InView } from 'react-intersection-observer';
import { objectToBase64 } from '@bgzt/utils';
import { useEnvironmentInfo } from '~/hooks';
import { goToFullWebviewOnApp } from '~/services/app-interface';
import { ReviewItem } from '~/apis/review';
import { Section, Spacing } from '~/components';
import { ShopReviewKeywords, ShopReviewThumbnail, ShopReviewContent } from '~/app/products/[pid]/_components';
import { shopReviewsOptions } from '~/app/products/[pid]/_queries';
import { useReviewGuideBottomSheet } from '~/app/products/[pid]/_hooks';

import globalStyles from '~/styles/utils.module.css';
import styles from './ShopReviews.module.css';

type Props = {
  shopUid: number;
};

export const ShopReviews = ({ shopUid }: Props) => {
  const { isWebview } = useEnvironmentInfo();
  const { data, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery(
    shopReviewsOptions(shopUid as number, { page: 0, size: 10 }, { enabled: !!shopUid }),
  );
  const shopReviewsData = data?.pages.flatMap((page) => page.data.reviews);
  const guideData = data?.pages[0].data.guide;
  const handleInfoClick = useReviewGuideBottomSheet(guideData);
  const handleProductNameClick = (pid?: number) => {
    if (!pid) return;
    const path = `/products/${pid}`;
    const url = isWebview ? process.env.NEXT_PUBLIC_DOMAIN + path : path;
    isWebview ? goToFullWebviewOnApp({ url }).go() : globalThis.window.open(url, '_blank');
  };

  const handleThumbnailClick = (images: ReviewItem['images']) => {
    if (!images || images.length < 1) return;

    const originalImages = images.reduce<string[]>((acc, image) => {
      if (image.origin) acc.push(image.origin);
      return acc;
    }, []);
    const data = objectToBase64({
      images: originalImages,
      index: 0,
    });
    const url = process.env.NEXT_PUBLIC_BUNJANG_COMMON_DOMAIN + '/media-viewer' + `?data=${data}`;
    if (isWebview) {
      goToFullWebviewOnApp({ url }).go();
      return;
    }
    globalThis.window.open(url, '_blank');
  };

  const convertGradePointScale = (grade: number) => {
    return (grade / 2).toFixed(1);
  };
  const handleInView = async (inView: boolean) => {
    if (!inView || !hasNextPage || isFetchingNextPage) return;
    await fetchNextPage();
  };

  return (
    <Section>
      <Spacing height={30} />
      <Flex gap={4} align="center">
        <Typography variant="L3" color="gray-900">
          전체 후기
        </Typography>
        <button className={styles.infoButton} onClick={handleInfoClick}>
          <SicInfoCircle size={16} color={theme.colors['gray-600']} />
        </button>
      </Flex>

      <Spacing height={30} />
      <ul className={styles.reviewList}>
        {shopReviewsData?.map((review) => {
          const hasContent = review.content;
          const hasKeywords = review.keywords && review.keywords.length > 0;
          return (
            <li key={review.id}>
              {/* 별점 */}
              <div className={styles.grade}>
                <IcStarFill size={16} fill={theme.colors['yellow-500']} />
                <Typography variant="L4" color="gray-900">
                  {convertGradePointScale(review.grade ?? 0)}
                </Typography>
              </div>

              <Spacing height={12} />

              <div className={styles.contentContainer}>
                {/* 컨텐츠, 이미지 */}
                {hasContent && (
                  <div className={styles.content}>
                    <ShopReviewContent content={review.content} />

                    {review.images?.[0].thumbnail?.small && (
                      <ShopReviewThumbnail images={review.images} onClick={() => handleThumbnailClick(review.images)} />
                    )}
                  </div>
                )}
                {hasContent && hasKeywords && <Spacing height={8} />}
                {/* 키워드 */}
                {hasKeywords && <ShopReviewKeywords keywords={review.keywords} />}

                <Spacing height={10} />
                {/* 후기작성자, 작성일 */}
                <Typography variant="L12" color="gray-300" className={styles.reviewerInfo}>
                  {review.writerName}
                  {review.shopBadgeUrl && (
                    <Image
                      src={review.shopBadgeUrl}
                      width={21}
                      height={12}
                      alt="상점 배지"
                      unoptimized
                      className={styles.shopBadgeImage}
                    />
                  )}
                  ・{review.modifiedAtBefore}
                </Typography>
                <Spacing height={12} />
                {/* 거래상품 */}
                <div className={styles.productNameContainer} onClick={() => handleProductNameClick(review.productId)}>
                  <div className={styles.productName}>
                    <Typography variant="L11" color="gray-500" className={styles.productLabel}>
                      거래상품
                    </Typography>
                    <Typography variant="L12" color="gray-500" className={globalStyles.singleLineEllipticalText}>
                      {review.productName}
                    </Typography>
                  </div>

                  <div className={styles.arrow}>
                    <IcChevronRight size={12} fill={theme.colors['gray-200']} />
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {!isFetching && !isFetchingNextPage && shopReviewsData && shopReviewsData?.length > 0 && hasNextPage && (
        <InView as="div" onChange={handleInView} />
      )}
      <Spacing height={30} />
    </Section>
  );
};
